import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import { useDebounceValue, useToggle } from 'usehooks-ts';

import SearchIcon from '@/assets/icons/search.svg?react';
import XIcon from '@/assets/icons/x-big.svg?react';

import TextInput, { TextInputProps } from './TextInput';

interface SearchInputProps extends Omit<TextInputProps, 'onChange'> {
  onChange?: (event: string) => void;
  debounceMs?: number;
}

const SearchInput = (props: SearchInputProps) => {
  const { debounceMs = 300, value: externalValue, onChange, ...restProps } = props;
  const [currentValue, setCurrentValue] = useState<string>(externalValue?.toString() || '');
  const [isFocused, toggleFocused] = useToggle(false);
  const isFilledOrFocused = currentValue || isFocused;

  // Keep latest onChange in a ref so the debounce effect can depend only on [debouncedValue];
  // listing onChange in deps would re-run the effect whenever the parent re-renders (e.g. form/watch),
  // causing unnecessary or repeated calls and potential render loops.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [debouncedValue] = useDebounceValue(currentValue, debounceMs);

  useEffect(() => {
    onChangeRef.current?.(debouncedValue);
  }, [debouncedValue]);

  const handleClear = useCallback(() => {
    setCurrentValue('');
    onChangeRef.current?.('');
  }, []);

  const handleOnChange = useCallback((value: string | number) => {
    const stringValue = String(value);
    setCurrentValue(stringValue);
  }, []);

  useEffect(() => {
    const externalValueString = externalValue?.toString() || '';
    if (externalValueString !== currentValue) {
      setCurrentValue(externalValueString);
    }
  }, [externalValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TextInput
      {...restProps}
      value={currentValue}
      onChange={handleOnChange}
      size="small"
      InputProps={{
        sx: { paddingX: 1, '& .MuiInputBase-input.MuiOutlinedInput-input': { paddingX: 0 } },
        onFocus: toggleFocused,
        onBlur: toggleFocused,
        startAdornment: !isFilledOrFocused && (
          <InputAdornment position="start" sx={{ color: 'grey.700' }}>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: isFilledOrFocused && (
          <InputAdornment position="end" sx={{ color: 'grey.700' }}>
            <IconButton onClick={handleClear}>
              <XIcon width={12} height={12} />
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
};
export default SearchInput;
